import React, { useState, useEffect } from 'react';
import { 
  getRouteTemplates, createRouteTemplate, updateRouteTemplate, deleteRouteTemplate,
  getRouteTemplate, addStoresToTemplateBulk, updateTemplateStoreOrder, removeStoreFromTemplate,
  getStores, getActiveChains, duplicateRouteTemplate, getMercaderistasWithRoutes, createAssignment,
  getUserRoutes
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Trash2, Route, Store, ChevronUp, ChevronDown, 
  Building2, Edit, Users, MapPin, Copy, UserPlus, Info,
  CheckCircle2, Circle, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function RouteTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [stores, setStores] = useState([]);
  const [chains, setChains] = useState([]);
  const [mercaderistas, setMercaderistas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selected template for editing
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateStores, setTemplateStores] = useState([]);
  
  // Create/Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  
  // Add stores dialog
  const [showAddStores, setShowAddStores] = useState(false);
  const [filterChain, setFilterChain] = useState('all');
  const [searchStore, setSearchStore] = useState('');
  const [selectedStores, setSelectedStores] = useState(new Set());

  // Assign users dialog
  const [showAssignUsers, setShowAssignUsers] = useState(false);
  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState(new Set());

  // Duplicate dialog
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [templatesRes, storesRes, chainsRes, mercaRes] = await Promise.all([
        getRouteTemplates(),
        getStores({ active: true }),
        getActiveChains(),
        getMercaderistasWithRoutes()
      ]);
      setTemplates(templatesRes.data);
      setStores(storesRes.data);
      setChains(chainsRes.data);
      setMercaderistas(mercaRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetails = async (templateId) => {
    try {
      const response = await getRouteTemplate(templateId);
      setSelectedTemplate(response.data);
      setTemplateStores(response.data.stores || []);
    } catch (error) {
      toast.error('Error cargando plantilla');
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: '', description: '' });
    setShowDialog(true);
  };

  const handleEditTemplate = (template, e) => {
    e?.stopPropagation();
    setEditingTemplate(template);
    setFormData({ 
      name: template.name, 
      description: template.description || '' 
    });
    setShowDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (editingTemplate) {
        await updateRouteTemplate(editingTemplate.id, formData);
        toast.success('Plantilla actualizada');
        if (selectedTemplate?.id === editingTemplate.id) {
          loadTemplateDetails(editingTemplate.id);
        }
      } else {
        const response = await createRouteTemplate(formData);
        toast.success('Plantilla creada - Ahora agrega tiendas');
        setSelectedTemplate(response.data);
        setTemplateStores([]);
      }
      setShowDialog(false);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando plantilla');
    }
  };

  const handleDeleteTemplate = async (template, e) => {
    e?.stopPropagation();
    if (!window.confirm(`¿Eliminar la plantilla "${template.name}"?`)) return;
    
    try {
      await deleteRouteTemplate(template.id);
      toast.success('Plantilla eliminada');
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(null);
        setTemplateStores([]);
      }
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando plantilla');
    }
  };

  const handleDuplicateTemplate = async (template, e) => {
    e?.stopPropagation();
    setEditingTemplate(template);
    setDuplicateName(`${template.name} (copia)`);
    setShowDuplicateDialog(true);
  };

  const confirmDuplicate = async () => {
    try {
      const response = await duplicateRouteTemplate(editingTemplate.id, duplicateName || null);
      toast.success(response.data.message);
      setShowDuplicateDialog(false);
      loadInitialData();
      // Select the new template
      if (response.data.template) {
        setSelectedTemplate(response.data.template);
        loadTemplateDetails(response.data.template.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error duplicando plantilla');
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    loadTemplateDetails(template.id);
  };

  // Add stores functions
  const openAddStoresDialog = () => {
    setSelectedStores(new Set());
    setFilterChain('all');
    setSearchStore('');
    setShowAddStores(true);
  };

  const getAvailableStores = () => {
    const templateStoreIds = new Set(templateStores.map(ts => ts.store_id));
    return stores.filter(s => !templateStoreIds.has(s.id));
  };

  const getFilteredStores = () => {
    let filtered = getAvailableStores();
    
    if (filterChain && filterChain !== 'all') {
      filtered = filtered.filter(s => s.chain_id === filterChain);
    }
    
    if (searchStore) {
      const search = searchStore.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(search) ||
        (s.code && s.code.toLowerCase().includes(search)) ||
        (s.address && s.address.toLowerCase().includes(search))
      );
    }
    
    return filtered;
  };

  const toggleStoreSelection = (storeId) => {
    const newSet = new Set(selectedStores);
    if (newSet.has(storeId)) {
      newSet.delete(storeId);
    } else {
      newSet.add(storeId);
    }
    setSelectedStores(newSet);
  };

  const handleAddSelectedStores = async () => {
    if (selectedStores.size === 0) return;
    
    try {
      await addStoresToTemplateBulk(selectedTemplate.id, Array.from(selectedStores));
      toast.success(`${selectedStores.size} tienda(s) agregada(s)`);
      setShowAddStores(false);
      loadTemplateDetails(selectedTemplate.id);
      loadInitialData();
    } catch (error) {
      toast.error('Error agregando tiendas');
    }
  };

  const handleRemoveStore = async (storeEntry) => {
    try {
      await removeStoreFromTemplate(selectedTemplate.id, storeEntry.id);
      toast.success('Tienda removida');
      loadTemplateDetails(selectedTemplate.id);
      loadInitialData();
    } catch (error) {
      toast.error('Error removiendo tienda');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    await moveItem(index, index - 1);
  };

  const handleMoveDown = async (index) => {
    if (index === templateStores.length - 1) return;
    await moveItem(index, index + 1);
  };

  const moveItem = async (fromIndex, toIndex) => {
    const newStores = [...templateStores];
    const [movedItem] = newStores.splice(fromIndex, 1);
    newStores.splice(toIndex, 0, movedItem);
    
    try {
      const updates = newStores.map((item, idx) => 
        updateTemplateStoreOrder(selectedTemplate.id, item.id, idx + 1)
      );
      await Promise.all(updates);
      await loadTemplateDetails(selectedTemplate.id);
      toast.success('Orden actualizado');
    } catch (error) {
      toast.error('Error reordenando');
    }
  };

  // Assign users functions
  const openAssignUsersDialog = () => {
    setSelectedUsersToAssign(new Set());
    setShowAssignUsers(true);
  };

  const getUnassignedUsers = () => {
    return mercaderistas.filter(m => !m.has_route || m.route_template_id !== selectedTemplate?.id);
  };

  const toggleUserSelection = (userId) => {
    const newSet = new Set(selectedUsersToAssign);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setSelectedUsersToAssign(newSet);
  };

  const handleAssignSelectedUsers = async () => {
    if (selectedUsersToAssign.size === 0) return;
    
    let assigned = 0;
    let failed = 0;
    for (const userId of selectedUsersToAssign) {
      try {
        await createAssignment({
          user_id: userId,
          route_template_id: selectedTemplate.id
        });
        assigned++;
      } catch {
        failed++;
      }
    }
    
    if (assigned > 0) {
      toast.success(`${assigned} mercaderista(s) asignado(s)`);
    }
    if (failed > 0) {
      toast.error(`${failed} asignación(es) fallaron`);
    }
    setShowAssignUsers(false);
    loadInitialData();
    loadTemplateDetails(selectedTemplate.id);
  };

  // Get step status for template
  const getTemplateSteps = (template) => {
    const hasStores = (template.store_count || 0) > 0;
    const hasUsers = (template.user_count || 0) > 0;
    return { hasStores, hasUsers };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="route-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Plantillas de Ruta</h1>
          <p className="text-slate-500">Crea rutas reutilizables y asígnalas a múltiples mercaderistas</p>
        </div>
        <Button onClick={handleCreateTemplate} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Plantilla
        </Button>
      </div>

      {/* Instructions */}
      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Flujo:</strong> 1️⃣ Crea una plantilla → 2️⃣ Agrega tiendas en orden → 3️⃣ Asigna a mercaderistas. 
          Puedes <strong>duplicar</strong> plantillas existentes para crear variaciones.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Templates List */}
        <Card className="lg:col-span-1 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Route className="w-5 h-5 text-blue-600" />
              Plantillas
            </CardTitle>
            <CardDescription>{templates.length} plantilla(s)</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[520px]">
              <div className="space-y-2">
                {templates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Route className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay plantillas</p>
                    <p className="text-sm">Crea tu primera plantilla de ruta</p>
                  </div>
                ) : (
                  templates.map((template) => {
                    const steps = getTemplateSteps(template);
                    return (
                      <div
                        key={template.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'bg-blue-50 border-blue-300 shadow-sm'
                            : 'hover:bg-slate-50 border-slate-200'
                        }`}
                        onClick={() => handleSelectTemplate(template)}
                        data-testid={`template-item-${template.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 truncate">{template.name}</h3>
                            {template.description && (
                              <p className="text-xs text-slate-500 truncate">{template.description}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress steps */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`flex items-center gap-1 text-xs ${steps.hasStores ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {steps.hasStores ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                            <span>{template.store_count || 0} tiendas</span>
                          </div>
                          <ArrowRight className="w-3 h-3 text-slate-300" />
                          <div className={`flex items-center gap-1 text-xs ${steps.hasUsers ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {steps.hasUsers ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                            <span>{template.user_count || 0} usuarios</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 pt-1 border-t border-slate-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={(e) => handleEditTemplate(template, e)}
                            data-testid={`edit-template-${template.id}`}
                          >
                            <Edit className="w-3 h-3 mr-1" /> Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-blue-600"
                            onClick={(e) => handleDuplicateTemplate(template, e)}
                            data-testid={`duplicate-template-${template.id}`}
                          >
                            <Copy className="w-3 h-3 mr-1" /> Duplicar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-red-500 hover:text-red-700"
                            onClick={(e) => handleDeleteTemplate(template, e)}
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right: Template Details */}
        <Card className="lg:col-span-2 border-slate-200">
          {!selectedTemplate ? (
            <CardContent className="py-16">
              <div className="text-center text-slate-500">
                <Route className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Selecciona una plantilla</h3>
                <p className="text-slate-500 mb-4">Para ver y editar las tiendas de la ruta</p>
                <Button onClick={handleCreateTemplate} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Crear Nueva Plantilla
                </Button>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      {selectedTemplate.name}
                    </CardTitle>
                    {selectedTemplate.description && (
                      <CardDescription>{selectedTemplate.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={openAssignUsersDialog} data-testid="assign-users-btn">
                      <UserPlus className="w-4 h-4 mr-1" /> Asignar Usuarios
                    </Button>
                    <Button size="sm" onClick={openAddStoresDialog} data-testid="add-stores-btn">
                      <Plus className="w-4 h-4 mr-1" /> Agregar Tiendas
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <Tabs defaultValue="stores" className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="stores" className="flex items-center gap-1">
                      <Store className="w-4 h-4" />
                      Tiendas ({templateStores.length})
                    </TabsTrigger>
                    <TabsTrigger value="users" className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Usuarios Asignados ({selectedTemplate.user_count || 0})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="stores">
                    <ScrollArea className="h-[400px]">
                      {templateStores.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                          <Store className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p className="font-medium">No hay tiendas en esta ruta</p>
                          <p className="text-sm mb-4">Agrega tiendas para definir el orden de visitas</p>
                          <Button onClick={openAddStoresDialog}>
                            <Plus className="w-4 h-4 mr-2" /> Agregar Tiendas
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {templateStores.map((item, index) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border"
                              data-testid={`template-store-${item.id}`}
                            >
                              {/* Order number */}
                              <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                                {index + 1}
                              </div>
                              
                              {/* Store info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {item.store?.code && (
                                    <span className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                      {item.store.code}
                                    </span>
                                  )}
                                  <span className="font-medium truncate">
                                    {item.store?.name || 'N/A'}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 truncate">
                                  {item.store?.address || ''}
                                </p>
                              </div>

                              {item.chain_name && (
                                <Badge variant="outline" className="text-xs shrink-0">
                                  {item.chain_name}
                                </Badge>
                              )}

                              {/* Reorder buttons */}
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  disabled={index === 0}
                                  onClick={() => handleMoveUp(index)}
                                  data-testid={`move-up-${item.id}`}
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  disabled={index === templateStores.length - 1}
                                  onClick={() => handleMoveDown(index)}
                                  data-testid={`move-down-${item.id}`}
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Remove button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRemoveStore(item)}
                                data-testid={`remove-store-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="users">
                    <ScrollArea className="h-[400px]">
                      {(selectedTemplate.user_count || 0) === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                          <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p className="font-medium">No hay mercaderistas asignados</p>
                          <p className="text-sm mb-4">Asigna esta ruta a mercaderistas</p>
                          <Button onClick={openAssignUsersDialog}>
                            <UserPlus className="w-4 h-4 mr-2" /> Asignar Usuarios
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {mercaderistas
                            .filter(m => m.route_template_id === selectedTemplate.id)
                            .map(user => (
                              <div
                                key={user.id}
                                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border"
                              >
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium">{user.name}</p>
                                  <p className="text-xs text-slate-500">{user.email}</p>
                                </div>
                                <Badge variant="secondary" className="text-xs">
                                  {templateStores.length} tiendas
                                </Badge>
                              </div>
                            ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Create/Edit Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla de Ruta'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate 
                ? 'Modifica los datos de la plantilla' 
                : 'Paso 1: Dale un nombre a tu plantilla'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Ruta Norte, Ruta Centro..."
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional de la ruta..."
                rows={3}
                data-testid="template-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTemplate} data-testid="save-template-btn">
              {editingTemplate ? 'Guardar Cambios' : 'Crear y Agregar Tiendas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Template Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Duplicar Plantilla
            </DialogTitle>
            <DialogDescription>
              Se creará una copia de "{editingTemplate?.name}" con todas sus tiendas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="duplicate-name">Nombre de la copia</Label>
              <Input
                id="duplicate-name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Nombre para la nueva plantilla"
                data-testid="duplicate-name-input"
              />
            </div>
            <Alert className="border-amber-200 bg-amber-50">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                La plantilla duplicada incluirá las <strong>{editingTemplate?.store_count || 0} tiendas</strong> en el mismo orden.
                Los usuarios NO se copiarán.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmDuplicate} data-testid="confirm-duplicate-btn">
              <Copy className="w-4 h-4 mr-2" /> Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stores Dialog */}
      <Dialog open={showAddStores} onOpenChange={setShowAddStores}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Paso 2: Agregar Tiendas a "{selectedTemplate?.name}"
            </DialogTitle>
            <DialogDescription>
              Selecciona las tiendas que formarán parte de esta ruta. Se agregarán al final de la lista.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-2">
              <Select value={filterChain} onValueChange={setFilterChain}>
                <SelectTrigger className="w-48">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por cadena" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las cadenas</SelectItem>
                  {chains.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Buscar tienda..."
                value={searchStore}
                onChange={(e) => setSearchStore(e.target.value)}
                className="flex-1"
              />
            </div>

            {/* Selection info */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">
                {selectedStores.size} seleccionada(s) de {getFilteredStores().length} disponibles
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    const all = new Set(getFilteredStores().map(s => s.id));
                    setSelectedStores(all);
                  }}
                >
                  Seleccionar todos
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedStores(new Set())}>
                  Limpiar
                </Button>
              </div>
            </div>

            {/* Store list */}
            <ScrollArea className="h-[300px] border rounded-lg">
              <div className="p-2 space-y-1">
                {getFilteredStores().length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    {getAvailableStores().length === 0 
                      ? 'Todas las tiendas ya están en la plantilla'
                      : 'No hay tiendas con estos filtros'}
                  </p>
                ) : (
                  getFilteredStores().map((store) => (
                    <div
                      key={store.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedStores.has(store.id) 
                          ? 'bg-blue-50 border border-blue-200' 
                          : 'hover:bg-slate-50 border border-transparent'
                      }`}
                      onClick={() => toggleStoreSelection(store.id)}
                    >
                      <Checkbox 
                        checked={selectedStores.has(store.id)}
                        onCheckedChange={() => toggleStoreSelection(store.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {store.code && (
                            <span className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                              {store.code}
                            </span>
                          )}
                          <span className="font-medium truncate">{store.name}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate">{store.address}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStores(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddSelectedStores}
              disabled={selectedStores.size === 0}
              data-testid="confirm-add-stores-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar {selectedStores.size > 0 ? `(${selectedStores.size})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={showAssignUsers} onOpenChange={setShowAssignUsers}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Paso 3: Asignar Mercaderistas
            </DialogTitle>
            <DialogDescription>
              Selecciona los mercaderistas que tendrán esta ruta asignada
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <ScrollArea className="h-[300px] border rounded-lg">
              <div className="p-2 space-y-1">
                {getUnassignedUsers().length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    Todos los mercaderistas ya tienen esta ruta o no hay mercaderistas disponibles
                  </p>
                ) : (
                  getUnassignedUsers().map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedUsersToAssign.has(user.id) 
                          ? 'bg-blue-50 border border-blue-200' 
                          : 'hover:bg-slate-50 border border-transparent'
                      }`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <Checkbox 
                        checked={selectedUsersToAssign.has(user.id)}
                        onCheckedChange={() => toggleUserSelection(user.id)}
                      />
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      {user.has_route && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                          Tiene otra ruta
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            
            {selectedUsersToAssign.size > 0 && (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  Los mercaderistas seleccionados recibirán la ruta "{selectedTemplate?.name}" 
                  con {templateStores.length} tiendas.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignUsers(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAssignSelectedUsers}
              disabled={selectedUsersToAssign.size === 0}
              data-testid="confirm-assign-users-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Asignar {selectedUsersToAssign.size > 0 ? `(${selectedUsersToAssign.size})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
